import { specFractionDigits, specValue } from '../cars/carFigures';

/**
 * The questions a duel may ask (SPEC-007).
 *
 * A question is a FIGURE plus a DIRECTION plus a MINIMUM MARGIN, and all three
 * are load-bearing.
 *
 * The direction is not always "bigger", which is the first thing the schoolyard
 * version of this game gets wrong when it is written down: the two most
 * interesting questions in this catalogue are the ones where less wins. A table
 * that only knew `higher` would either drop 0–100 and weight or — worse — ask
 * which car is SLOWER to 100 km/h, a question that is perfectly answerable and
 * that nobody has ever asked.
 *
 * The minimum margin is what keeps the game from being a coin toss dressed as a
 * question. The F40 and the Diablo are one km/h apart at the top end; nobody
 * knows that, nobody could know that, and losing a nine-round streak to it is
 * the fastest way to make a reader close the screen. The bars are ABSOLUTE and
 * per figure rather than a shared percentage, because a percentage is strict
 * about 0–100 (where a tenth of a second is a real difference) and generous
 * about displacement (where 300cc is a rounding step between two V8s).
 *
 * Every figure is read through `carFigures`, the one place the path to a
 * stored number is written down. A second copy of
 * `specs.zeroToHundred.seconds` in here is how a catalogue change breaks one
 * screen and not the other.
 */

/** Which end of the figure wins the question. */
export const DUEL_DIRECTIONS = ['higher', 'lower'];

export const DUEL_SPECS = [
    // --- Tier 1: a child can have an opinion from the pictures alone ------
    // 10 km/h is about the width of a speedometer's own honesty at this end of
    // the range, and it separates the three pairs in the catalogue that sit
    // within five of each other.
    { key: 'topSpeed', wins: 'higher', minimumMargin: 10, difficulty: 1 },
    // 20 hp: the 993 and the NSX are two apart, which is a question about which
    // press release you read rather than about the cars.
    { key: 'power', wins: 'higher', minimumMargin: 20, difficulty: 1 },
    // Two cylinders, because one is a question nobody could answer and because
    // the catalogue only ever steps 4-6-8-10-12. The easiest question in the
    // table for a reader who cannot yet read: a V12 badge is on the car.
    { key: 'cylinders', wins: 'higher', minimumMargin: 2, difficulty: 1 },

    // --- Tier 2: needs a sense of the cars rather than of the numbers -----
    // 12 cm. These are all within 37 cm of each other, so the bar is tight in
    // absolute terms and still wide enough that the answer is a shape rather
    // than a specification.
    { key: 'length', wins: 'higher', minimumMargin: 120, difficulty: 2 },
    // 60 kg is roughly a passenger, and it is the point at which the difference
    // is something the catalogue's own weight bases could not have invented —
    // dry against kerb is worth about that much on its own (SPEC-002 D-6).
    { key: 'weight', wins: 'lower', minimumMargin: 60, difficulty: 2 },
    // A tenth is a launch technique; three tenths is a car. This is the tightest
    // bar in the table and one of the two figures with a decimal, which is why
    // the margin is rounded before it is compared.
    { key: 'zeroToHundred', wins: 'lower', minimumMargin: 0.3, difficulty: 2 },
    // 300cc, because these are 1.3- to 8-litre engines and anything tighter is a
    // question about bore and stroke rather than about which engine is bigger.
    { key: 'displacement', wins: 'higher', minimumMargin: 300, difficulty: 2 },

    // --- Tier 3: specialist. These are the ones that end a long run -------
    { key: 'torque', wins: 'higher', minimumMargin: 30, difficulty: 3 },
    // The figure nobody publishes and everybody argues about. It is the reveal's
    // own explanation for four of the other questions, which is what earns it a
    // place as a question of its own once a reader has seen it a few times.
    { key: 'powerToWeight', wins: 'higher', minimumMargin: 30, difficulty: 3 },
    // Ten of the twenty-four have a published standing quarter; the rest never
    // appear under this question, which is a delivery gap rather than a slow car.
    { key: 'quarterMile', wins: 'lower', minimumMargin: 0.4, difficulty: 3 },
    // FEWER is rarer, which is the one question in the table where the reader
    // has to invert the number themselves. Half the catalogue carries no
    // production figure at all and simply never appears under it.
    { key: 'production', wins: 'lower', minimumMargin: 100, difficulty: 3 },
];

const BY_KEY = new Map(DUEL_SPECS.map((spec) => [spec.key, spec]));

export function duelSpec(key) {
    return BY_KEY.get(key) || null;
}

export function listDuelSpecKeys() {
    return DUEL_SPECS.map(({ key }) => key);
}

/**
 * The whole question as a sentence, for a reader who is being read to.
 *
 * The screen draws the question as three pictogram chips — a question mark, a
 * direction and a subject — because a nine-year-old with no English has to be
 * able to play (SPEC-007 D-36). That drawing is exactly what a screen reader
 * cannot use, so the sentence stays, hidden, as the heading the pair of cards is
 * labelled by. Two renderings of one question is the correct shape here, not a
 * duplication to tidy away.
 */
export function questionKey(specKey) {
    return `duel.question.${specKey}`;
}

/**
 * "Which car has more" / "Which car has less" — the lead-in, as one sentence.
 *
 * Keyed off the DIRECTION rather than off the figure, so eleven questions share
 * two lines and a twelfth subject needs no new copy for its opening.
 *
 * One message rather than "Which car has" plus a separate "more": split, a
 * translator is handed two fragments to reassemble in an order this component
 * decided for them, and the languages that put the comparative first would have
 * to be fought rather than translated.
 */
export function leadKey(specKey) {
    return `duel.lead.${duelSpec(specKey)?.wins ?? 'higher'}`;
}

/**
 * Round to the precision the catalogue published this figure to.
 *
 * `5.9 - 5.6` is `0.2999999999999998`, which fails a `>= 0.3` test — so without
 * this, a pairing that clears the tightest bar in the table exactly would be
 * rejected, and it would be rejected only for the one figure that has a decimal.
 */
function roundToFigure(value, specKey) {
    const step = 10 ** specFractionDigits(specKey);

    return Math.round(value * step) / step;
}

/**
 * How far apart these two cars are on this figure, or `null` where either of
 * them does not carry it.
 */
export function duelMargin(carA, carB, specKey) {
    const left = specValue(carA, specKey);
    const right = specValue(carB, specKey);

    if (left === null || right === null) {
        return null;
    }

    return roundToFigure(Math.abs(left - right), specKey);
}

/**
 * Which of these two wins this question — or `null` where there is no question
 * to ask.
 *
 * Null covers three cases and they are deliberately the same case: a figure one
 * of them does not carry, an outright tie, and a gap inside the minimum margin.
 * All three mean "this is not something a reader could be right about", and
 * picking a winner by id in any of them would be the app inventing a fact.
 */
export function duelWinner(carA, carB, specKey) {
    const spec = duelSpec(specKey);
    const margin = spec ? duelMargin(carA, carB, specKey) : null;

    if (margin === null || margin < spec.minimumMargin) {
        return null;
    }

    const left = specValue(carA, specKey);
    const right = specValue(carB, specKey);

    if (spec.wins === 'higher') {
        return left > right ? carA : carB;
    }

    return left < right ? carA : carB;
}

/**
 * This car's figure as a fraction of the pair's TOTAL.
 *
 * The reveal draws one horizontal bar split between the two cars, so these are
 * the two pieces of it and they add up to exactly one. A split bar is a
 * stronger picture than two bars on a shared scale for the reason a tug of war
 * is: there is a middle, and the distance the split sits from it IS the
 * difference. Two separate tracks make the reader compare two lengths; one
 * track makes the comparison the object.
 *
 * The raw figure, never an inverted "advantage": on "which one is lighter" the
 * heavier car genuinely takes the larger share of the bar, and it lost. Length
 * is the QUANTITY and colour is the ANSWER, which is what lets one drawing serve
 * all seven questions — including "which is rarer", where the winner's share is
 * a sliver and that sliver is the whole point.
 */
export function duelShare(value, total) {
    if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0 || value <= 0) {
        return 0;
    }

    return Math.min(value / total, 1);
}
